import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import Card from 'react-bootstrap/Card';
import Badge from 'react-bootstrap/Badge';

const AdminImportPage = () => {
    const [parentCategories, setParentCategories] = useState([]);
    const [childCategories, setChildCategories] = useState([]);
    const [selectedParentId, setSelectedParentId] = useState('');
    const [selectedChildId, setSelectedChildId] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [importing, setImporting] = useState(false);
    const [importResults, setImportResults] = useState(null);
    const [error, setError] = useState('');
    const { encryptedApiCall, API_BASE_URL, isAdmin, logout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isAdmin) {
            navigate('/categories');
            return;
        }
        fetchParentCategories();
    }, [isAdmin]);

    const fetchParentCategories = async () => {
        try {
            const data = await encryptedApiCall(`${API_BASE_URL}/categories?level=1`, {
                method: 'GET'
            });
            setParentCategories(data);
        } catch (err) {
            if (err.message && err.message.includes('401')) {
                logout();
                navigate('/login');
                return;
            }
            setError(err.message || 'Failed to fetch categories');
        }
    };

    const fetchChildCategories = async (parentId) => {
        try {
            const data = await encryptedApiCall(`${API_BASE_URL}/categories?parentId=${parentId}`, {
                method: 'GET'
            });
            setChildCategories(data);
            setSelectedChildId(''); // Reset child selection
        } catch (err) {
            setError(err.message || 'Failed to fetch child categories');
        }
    };

    const handleParentChange = (e) => {
        const parentId = e.target.value;
        setSelectedParentId(parentId);
        if (parentId) {
            fetchChildCategories(parentId);
        } else {
            setChildCategories([]);
            setSelectedChildId('');
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setSelectedFiles(files);
        setImportResults(null);
    };

    /**
     * Convert string to base64, handling Unicode characters properly
     * @param {string} str - String to encode
     * @returns {string} - Base64 encoded string
     */
    const stringToBase64 = (str) => {
        // Convert string to UTF-8 bytes, then to base64
        // This handles Unicode characters correctly
        const utf8Bytes = new TextEncoder().encode(str);
        let binary = '';
        const chunkSize = 0x8000; // 32KB chunks to avoid stack overflow
        
        for (let i = 0; i < utf8Bytes.length; i += chunkSize) {
            const chunk = utf8Bytes.subarray(i, Math.min(i + chunkSize, utf8Bytes.length));
            binary += String.fromCharCode.apply(null, Array.from(chunk));
        }
        
        return btoa(binary);
    };

    const readFileAsText = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                try {
                    // Parse JSON to validate it's valid
                    const jsonContent = JSON.parse(reader.result);
                    // Send as base64-encoded string for encryption compatibility
                    // Use UTF-8 safe encoding to handle Unicode characters
                    const base64 = stringToBase64(JSON.stringify(jsonContent));
                    resolve({
                        name: file.name,
                        content: base64
                    });
                } catch (err) {
                    reject(new Error(`Invalid JSON in ${file.name}: ${err.message}`));
                }
            };
            reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
            reader.readAsText(file);
        });
    };

    const handleImport = async () => {
        if (!selectedChildId || selectedFiles.length === 0) {
            setError('Please select a child category and at least one file');
            return;
        }

        setImporting(true);
        setError('');
        setImportResults(null);

        try {
            // Read all files as text and convert to base64
            const fileData = await Promise.all(
                selectedFiles.map(file => readFileAsText(file))
            );

            // Send import request
            const result = await encryptedApiCall(
                `${API_BASE_URL}/admin/import/questions`,
                {
                    method: 'POST'
                },
                {
                    categoryId: parseInt(selectedChildId),
                    files: fileData
                }
            );

            setImportResults(result.results);
        } catch (err) {
            setError(err.message || 'Failed to import questions');
        } finally {
            setImporting(false);
        }
    };

    const canImport = selectedParentId && selectedChildId && selectedFiles.length > 0 && !importing;

    return (
        <Container className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => navigate('/categories')} 
                        className="me-2"
                    >
                        ← Back to Categories
                    </Button>
                    <h2 className="d-inline ms-3">Bulk Question Import</h2>
                </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Card>
                <Card.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <strong>Parent Category (Level 1)</strong>
                            </Form.Label>
                            <Form.Select
                                value={selectedParentId}
                                onChange={handleParentChange}
                                required
                            >
                                <option value="">Select parent category...</option>
                                {parentCategories.map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                <strong>Child Category (Level 2)</strong>
                            </Form.Label>
                            <Form.Select
                                value={selectedChildId}
                                onChange={(e) => setSelectedChildId(e.target.value)}
                                disabled={!selectedParentId}
                                required
                            >
                                <option value="">Select child category...</option>
                                {childCategories.map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </option>
                                ))}
                            </Form.Select>
                            {!selectedParentId && (
                                <Form.Text className="text-muted">
                                    Please select a parent category first
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                <strong>JSON Files</strong>
                            </Form.Label>
                            <Form.Control
                                type="file"
                                accept=".json"
                                multiple
                                onChange={handleFileChange}
                                disabled={importing}
                            />
                            <Form.Text className="text-muted">
                                Select one or more JSON files containing questions
                            </Form.Text>
                            {selectedFiles.length > 0 && (
                                <div className="mt-2">
                                    <strong>Selected files ({selectedFiles.length}):</strong>
                                    <ul className="mb-0">
                                        {selectedFiles.map((file, index) => (
                                            <li key={index}>{file.name}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </Form.Group>

                        <Button
                            variant="primary"
                            onClick={handleImport}
                            disabled={!canImport}
                            size="lg"
                        >
                            {importing ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Importing...
                                </>
                            ) : (
                                'Import Questions'
                            )}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>

            {importResults && (
                <Card className="mt-4">
                    <Card.Header>
                        <h5>Import Results</h5>
                    </Card.Header>
                    <Card.Body>
                        <div className="mb-3">
                            <strong>Summary:</strong>
                            <ul>
                                <li>Total Files: {importResults.totalFiles}</li>
                                <li>Total Questions: {importResults.totalQuestions}</li>
                                <li>
                                    <Badge bg="success">Imported: {importResults.imported}</Badge>
                                </li>
                                <li>
                                    <Badge bg="warning">Skipped: {importResults.skipped}</Badge>
                                </li>
                            </ul>
                        </div>

                        {importResults.fileResults.map((fileResult, index) => (
                            <Card key={index} className="mb-2">
                                <Card.Header>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>{fileResult.fileName}</strong>
                                            {fileResult.testName && (
                                                <span className="text-muted ms-2">
                                                    → Test: <strong>{fileResult.testName}</strong>
                                                </span>
                                            )}
                                        </div>
                                        <div>
                                            {fileResult.success && (
                                                <Badge bg="success" className="me-2">Success</Badge>
                                            )}
                                            <Badge bg={fileResult.imported > 0 ? "success" : "secondary"} className="me-2">
                                                {fileResult.imported} imported
                                            </Badge>
                                            {fileResult.skipped > 0 && (
                                                <Badge bg="warning">
                                                    {fileResult.skipped} skipped
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </Card.Header>
                                {fileResult.errors.length > 0 && (
                                    <Card.Body>
                                        <strong>Errors:</strong>
                                        <ul className="mb-0">
                                            {fileResult.errors.map((error, errIndex) => (
                                                <li key={errIndex} className="text-danger small">
                                                    {error}
                                                </li>
                                            ))}
                                        </ul>
                                    </Card.Body>
                                )}
                            </Card>
                        ))}
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
};

export default AdminImportPage;

